/**
 * tourSteps/taskListSteps.js
 * 100% comprehensive tour covering the entire Task Management workspace:
 * Workspace Header, Multi-Filters, Pipeline Funnel, Workload Distribution, AI Intelligence Center,
 * Task View Tabs, Search & "+ New Task", and exhaustive column-by-column Table Breakdown.
 */

export const taskListSteps = [
    {
        title: 'Project Management Workspace',
        description: 'Your central command hub for enterprise resource planning, cross-functional project tracking, and team performance metrics.',
        selector: '[data-tour="workspace-header"]',
        fallbackSelector: '[data-tour="dashboard-filters"]',
        placement: 'bottomLeft',
    },
    {
        title: 'Multi-Dimensional Workspace Filters',
        description: 'Instantly isolate work across your enterprise. Filter tasks by specific project, responsible team member, milestone owner, or custom date ranges.',
        selector: '[data-tour="dashboard-filters"]',
        placement: 'bottom',
    },
    {
        title: 'Pipeline Funnel Cards & Stage Metrics',
        description: 'Real-time visibility across all 6 workflow stages: Backlog, In Progress, Under Review, Completed, On Hold, and Cancelled. Shows live task volume, estimated vs actual hours, and team member avatars. Click any card to filter the entire board instantly.',
        selector: '[data-tour="pipeline-funnel"]',
        placement: 'bottom',
    },
    {
        title: 'Workload Distribution & Team Capacity',
        description: 'Dynamic stacked bar chart mapping task distribution per team member. Prevents burnout by balancing allocations, tracks assigned headcount, and links directly to productivity reports.',
        selector: '[data-tour="workload-chart"]',
        placement: 'bottom',
    },
    {
        title: 'AI Intelligence Center & Risk Radar',
        description: 'Automated AI monitoring that identifies critical schedule bottlenecks, past-due deliverables, and blocked tasks. Switch between Action Alerts and Priority Ranking to triage urgent risks.',
        selector: '[data-tour="ai-intelligence-center"]',
        placement: 'left',
    },
    {
        title: 'Dynamic Task View Switcher',
        description: 'Seamlessly switch execution views depending on your workflow: List Table (dense operational tracking), Kanban Board (visual agile lanes), Timeline (Gantt roadmap), or Sprint (cadence cycles).',
        selector: '[data-tour="task-view-tabs"]',
        placement: 'bottomLeft',
    },
    {
        title: 'Instant Search & "+ New Task" Creator',
        description: 'Search tasks by keyword or unique #ID. Click "+ New Task" to launch the task creation drawer with custom assignees, priority, milestone associations, and checklists.',
        selector: '[data-tour="task-search-and-create"]',
        fallbackSelector: '[data-tour="new-task-btn"]',
        placement: 'bottomRight',
    },
    {
        title: 'Task Directory & Operational Table',
        description: 'The master tabular database. Every row represents an active work item with inline status editing, effort tracking gauges, and context-menu actions.',
        selector: '[data-tour="task-list-table"]',
        placement: 'top',
    },
    {
        title: 'Column 1: Title, ID & Work Item Indicator',
        description: 'Displays the task title, unique identifier (#ID), and item type. A circle ○ indicates an operational Task (color-coded by stage), while a gold diamond ◆ designates a high-level Milestone. Clicking opens the detailed Drawer.',
        selector: '[data-tour="col-title"]',
        fallbackSelector: '[data-tour="table-task-row"]',
        placement: 'bottomLeft',
    },
    {
        title: 'Column 2: Assignee / Milestone Owner',
        description: 'Displays the responsible employee with their photo avatar and name. For Milestones, this highlights the designated Milestone Owner accountable for deliverable sign-offs.',
        selector: '[data-tour="col-assignee"]',
        placement: 'bottom',
    },
    {
        title: 'Column 3: Associated Project',
        description: 'Identifies the parent project initiative. Clicking the project tag immediately filters the entire dashboard to only show work belonging to that initiative.',
        selector: '[data-tour="col-project"]',
        placement: 'bottom',
    },
    {
        title: 'Column 4: Priority Level',
        description: 'Visual urgency indicator: Critical (red), High (rose), Medium (blue), or Low (slate). Keeps the team aligned on top-priority blockers and daily deliverables.',
        selector: '[data-tour="col-priority"]',
        placement: 'bottom',
    },
    {
        title: 'Column 5: Stage / Execution Status',
        description: 'Interactive status selector (Backlog, In Progress, Under Review, Completed, On Hold, Cancelled). Changing status here automatically syncs across timers, sprint boards, and client reporting.',
        selector: '[data-tour="col-stage"]',
        placement: 'bottom',
    },
    {
        title: 'Column 6: Est / Effort & Overrun Gauge',
        description: 'Visualizes Estimated Hours against Actual Logged Hours (e.g. "12h / 8.5h"). The progress bar turns red if logged work exceeds estimated budget, protecting project margins.',
        selector: '[data-tour="col-effort"]',
        placement: 'bottom',
    },
    {
        title: 'Column 7: Due Date & Deadlines',
        description: 'Target completion deadline with relative badges (e.g., "Tomorrow", "In 3 days"). Past-due items display high-visibility alerts to prevent schedule slippage.',
        selector: '[data-tour="col-due"]',
        placement: 'bottom',
    },
    {
        title: 'Column 8: Quick Row Actions',
        description: 'Fast-action shortcuts: Quick Edit details, Add Subtasks, Convert Item Type (Task ↔ Milestone), or Delete the item (role permissions enforced).',
        selector: '[data-tour="col-actions"]',
        placement: 'bottomRight',
    },
    {
        title: 'Interactive Row & Context Menu',
        description: 'Click any table row to open the complete Task & Milestone Drawer. Right-click any row to open the instant Context Menu for quick status changes, subtask additions, or conversions.',
        selector: '[data-tour="table-task-row"]',
        placement: 'bottom',
    },
    {
        title: 'Dynamic Pagination & Record Totals',
        description: 'Browse through extensive project task inventories with flexible page limits and live total record counts.',
        selector: '[data-tour="table-pagination"]',
        fallbackSelector: '[data-tour="task-list-table"]',
        placement: 'top',
    },
];
